// The canonical, countersignable capacity-reservation PDF. Built with pdfmake — this project does
// not have jsPDF installed, so pdfmake (already a dependency here) is used instead. It draws real
// vector text and lines the same way jsPDF would: no DOM/canvas rasterization, a genuine selectable
// PDF a few KB in size, independent of how the page happens to be rendering.
//
// Font note: Mona Sans isn't bundled as a font asset in this project (no TTF available to embed),
// so this falls back to pdfmake's built-in Roboto family rather than Helvetica. Every string still
// runs through foldToAscii() as a defensive measure in case a future font swap lands on a WinAnsi-only
// face.
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { foldToAscii } from '@generated/utils/pdf-text';
import { AGREEMENT_BULLETS } from '@generated/utils/terms';

const vfs = (pdfFonts as any)?.vfs ?? (pdfFonts as any)?.pdfMake?.vfs;
if (vfs) (pdfMake as any).vfs = vfs;

const LOGO_URL = 'https://files-public.monday.com/use1/90515cd5-3bf5-42c7-b8e8-9206f988e523/BYFUSION-LOGO-RGB-01_1080.png';
// pdfmake can only embed images as data URLs, not remote URLs — fetch once and cache the conversion.
let logoDataUrlPromise: Promise<string | null> | null = null;
function getLogoDataUrl(): Promise<string | null> {
  if (!logoDataUrlPromise) {
    logoDataUrlPromise = fetch(LOGO_URL)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          }),
      )
      .catch(() => null);
  }
  return logoDataUrlPromise;
}

const RED = '#BF2A29';
const DARK = '#1a1a1a';
const GREY = '#5f5e5a';
const HAIRLINE = '#d6d4cc';
const CONTENT_WIDTH = 516; // 612 (US Letter pt width) - 48 - 48

export interface ReservationPdfMaterialLine {
  name: string;
  displayWeight: string;
}

export interface ReservationPdfSnapshot {
  referenceNumber: string;
  preparedDate: Date;
  ratesEffectiveDate: Date | null;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  shippingOrigin: string;
  formatName: string;
  frequencyLabel: string;
  commitmentLabel: string;
  unit: 'lbs' | 'tons';
  materialLines: ReservationPdfMaterialLine[];
  totalWeightLbsLabel: string;
  transportationLabel: string;
  desiredShipmentDateLabel: string;
  shipmentSizeSentence: string;
  periodCostLabel: string | null;
  periodCostValue: string | null;
  annualCostLabel: string;
  annualCostValue: string | null;
  costBasisNote: string;
  signatoryName: string;
  signatoryTitle: string;
  signatureDateLabel: string;
}

function f(text: string | null | undefined): string {
  return foldToAscii(text ?? '');
}

function fmtDate(d: Date | null): string {
  if (!d) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function hairline(width: number = CONTENT_WIDTH): any {
  return { canvas: [{ type: 'line', x1: 0, y1: 0, x2: width, y2: 0, lineWidth: 0.75, lineColor: HAIRLINE }] };
}

function sectionHeader(label: string): any {
  return {
    margin: [0, 16, 0, 6],
    stack: [{ text: f(label.toUpperCase()), style: 'sectionLabel' }, { margin: [0, 3, 0, 0], ...hairline() }],
  };
}

function fieldRows(rows: [string, string][]): any {
  return {
    table: {
      widths: [140, '*'],
      dontBreakRows: true,
      body: rows.map(([label, value]) => [
        { text: f(label.toUpperCase()), style: 'label', border: [false, false, false, false] },
        { text: f(value), style: 'value', border: [false, false, false, false] },
      ]),
    },
    layout: { paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 2, paddingBottom: () => 2 },
  };
}

function signatureBlock(opts: {
  heading: string;
  filled: boolean;
  signatureText?: string;
  nameValue?: string;
  titleValue?: string;
  dateValue?: string;
}): any {
  const { heading, filled, signatureText, nameValue, titleValue, dateValue } = opts;
  const lineWidth = 220;
  const stack: any[] = [{ text: f(heading), style: 'blockHeading', margin: [0, 0, 0, filled ? 6 : 0] }];

  if (filled) {
    stack.push({ text: f(signatureText || ' '), italics: true, fontSize: 14, margin: [0, 0, 0, 4] });
    stack.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: lineWidth, y2: 0, lineWidth: 0.75, lineColor: HAIRLINE }] });
    stack.push({
      margin: [0, 8, 0, 0],
      columns: [
        { width: '*', text: [{ text: 'NAME  ', style: 'tinyLabel' }, { text: f(nameValue), style: 'tinyValue' }] },
      ],
    });
    stack.push({
      margin: [0, 3, 0, 0],
      columns: [
        { width: '*', text: [{ text: 'TITLE  ', style: 'tinyLabel' }, { text: f(titleValue), style: 'tinyValue' }] },
      ],
    });
    stack.push({
      margin: [0, 3, 0, 0],
      columns: [
        { width: '*', text: [{ text: 'DATE  ', style: 'tinyLabel' }, { text: f(dateValue), style: 'tinyValue' }] },
      ],
    });
  } else {
    // At least 40pt of genuinely clear space above the rule — nothing drawn in it.
    stack.push({ text: ' ', margin: [0, 40, 0, 0] });
    stack.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: lineWidth, y2: 0, lineWidth: 0.75, lineColor: HAIRLINE }] });
    const blankLine = (label: string) => ({
      margin: [0, 10, 0, 0],
      stack: [
        { text: label, style: 'tinyLabel' },
        {
          margin: [0, 10, 0, 0],
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: lineWidth, y2: 0, lineWidth: 0.5, lineColor: HAIRLINE }],
        },
      ],
    });
    stack.push(blankLine('NAME'));
    stack.push(blankLine('TITLE'));
    stack.push(blankLine('DATE'));
  }

  return { width: '48%', stack };
}

export function buildReservationDocDefinition(s: ReservationPdfSnapshot, logoDataUrl?: string | null): any {
  const materialRows: [string, string][] = s.materialLines.map((m) => [m.name, `${m.displayWeight} ${s.unit}`]);

  return {
    pageSize: 'LETTER',
    pageMargins: [48, 48, 48, 78],
    defaultStyle: { font: 'Roboto', fontSize: 10, color: DARK },
    styles: {
      sectionLabel: { fontSize: 8, bold: true, color: GREY, characterSpacing: 0.4 },
      label: { fontSize: 8, color: GREY },
      value: { fontSize: 10, color: DARK },
      blockHeading: { fontSize: 9, bold: true, color: DARK },
      tinyLabel: { fontSize: 7, color: GREY, bold: true },
      tinyValue: { fontSize: 9, color: DARK },
      footerText: { fontSize: 7, color: GREY },
      wordmark: { fontSize: 20, bold: true, color: RED },
      mastheadSub: { fontSize: 8, color: GREY },
      mastheadRight: { fontSize: 8, color: GREY, alignment: 'right' },
      companyName: { fontSize: 16, bold: true, color: DARK },
      companySub: { fontSize: 10, color: GREY },
      bodySmall: { fontSize: 9, color: DARK, lineHeight: 1.3 },
      basisNote: { fontSize: 8, color: GREY, italics: true, lineHeight: 1.3 },
    },
    content: [
      {
        // Two independent blocks, each anchored to its own page margin — neither is positioned
        // relative to the other's width or content length. The left block is a fixed-width column
        // (matching the logo's own width) starting at the page's left content margin. The right
        // block is a '*' (fill-remaining) column, so it always spans through to the page's actual
        // right content margin regardless of how wide its own text is — each line inside it is
        // right-aligned to THAT column's right edge, which is the true content-right position
        // (equivalent to jsPDF's `doc.text(text, pageWidth - rightMargin, y, { align: 'right' })`).
        columns: [
          logoDataUrl
            ? { width: 130, stack: [{ image: logoDataUrl, width: 130 }, { text: 'Capacity Reservation', style: 'mastheadSub', margin: [0, 4, 0, 0] }] }
            : { width: 130, stack: [{ text: 'ByFusion', style: 'wordmark' }, { text: 'Capacity Reservation', style: 'mastheadSub', margin: [0, 2, 0, 0] }] },
          {
            width: '*',
            stack: [
              { text: f(s.referenceNumber), style: 'mastheadRight' },
              { text: `Prepared ${fmtDate(s.preparedDate)}`, style: 'mastheadRight' },
              { text: `Rates effective ${s.ratesEffectiveDate ? fmtDate(s.ratesEffectiveDate) : 'not set'}`, style: 'mastheadRight' },
            ],
          },
        ],
      },
      { margin: [0, 10, 0, 16], canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1.6, lineColor: RED }] },

      { text: f(s.companyName), style: 'companyName', margin: [0, 0, 0, 16] },

      sectionHeader('Company'),
      fieldRows([
        ['Company', s.companyName],
        ['Contact', s.contactName],
        ['Email', s.email],
        ['Phone', s.phone],
        ['Shipping origin', s.shippingOrigin],
      ]),

      sectionHeader('Material'),
      {
        table: {
          widths: [140, '*'],
          dontBreakRows: true,
          body: [
            ...materialRows.map(([label, value]) => [
              { text: f(label), style: 'value', border: [false, false, false, false] },
              { text: f(value), style: 'value', border: [false, false, false, false] },
            ]),
            [
              { text: 'TOTAL MONTHLY WEIGHT', style: 'label', bold: true, border: [false, true, false, false], borderColor: [HAIRLINE, HAIRLINE, HAIRLINE, HAIRLINE], margin: [0, 4, 0, 0] },
              { text: f(`${s.totalWeightLbsLabel} lbs`), style: 'value', bold: true, border: [false, true, false, false], borderColor: [HAIRLINE, HAIRLINE, HAIRLINE, HAIRLINE], margin: [0, 4, 0, 0] },
            ],
          ],
        },
        layout: { paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 2, paddingBottom: () => 2 },
      },

      sectionHeader('Shipping method and logistics'),
      fieldRows([
        ['Shipping method', s.formatName],
        ['Shipping frequency', s.frequencyLabel],
        ['Transportation coordination', s.transportationLabel],
        ['Target first shipment date', s.desiredShipmentDateLabel],
        ['Commitment length', s.commitmentLabel],
        ['Estimated shipment size', s.shipmentSizeSentence],
      ]),

      sectionHeader('Cost estimate'),
      fieldRows(
        [
          s.periodCostLabel ? [s.periodCostLabel, s.periodCostValue ?? '-'] : null,
          [s.annualCostLabel, s.annualCostValue ?? '-'],
        ].filter(Boolean) as [string, string][],
      ),

      sectionHeader('Terms'),
      {
        stack: AGREEMENT_BULLETS.map((bullet) => ({
          columns: [
            { width: 10, text: '-', style: 'bodySmall' },
            { width: '*', text: f(bullet), style: 'bodySmall' },
          ],
          margin: [0, 0, 0, 5],
        })),
      },

      {
        unbreakable: true,
        margin: [0, 20, 0, 0],
        stack: [
          { text: 'AUTHORIZATION', style: 'sectionLabel', margin: [0, 0, 0, 6] },
          hairline(),
          {
            margin: [0, 14, 0, 0],
            columns: [
              signatureBlock({
                heading: 'Partner',
                filled: true,
                signatureText: s.signatoryName,
                nameValue: s.signatoryName,
                titleValue: s.signatoryTitle,
                dateValue: s.signatureDateLabel,
              }),
            ],
          },
        ],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      margin: [48, 8, 48, 0],
      stack: [
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 0.75, lineColor: HAIRLINE }] },
        {
          margin: [0, 4, 0, 0],
          columns: [
            { width: '*', text: 'ByFusion Global, Inc.  \u00B7  operations@byfusion.com  \u00B7  +1 833 BYBLOCK  \u00B7  byfusion.com', style: 'footerText' },
            { width: 'auto', text: `Page ${currentPage} of ${pageCount}`, style: 'footerText' },
          ],
        },
        { text: 'Confidential between the parties. This reservation is not a services agreement.', style: 'footerText', margin: [0, 2, 0, 0] },
      ],
    }),
  };
}

/** Generates the canonical PDF client-side and resolves with it as a Blob (real vector text, no rasterization). */
export async function generateReservationPdfBlob(snapshot: ReservationPdfSnapshot): Promise<Blob> {
  const logoDataUrl = await getLogoDataUrl();
  return new Promise((resolve, reject) => {
    try {
      const doc = buildReservationDocDefinition(snapshot, logoDataUrl);
      (pdfMake as any).createPdf(doc).getBlob((blob: Blob) => resolve(blob));
    } catch (e) {
      reject(e);
    }
  });
}

/** Triggers a real browser download of the blob — used so the partner gets the file even if they close the tab. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Opens the PDF in a new browser tab so the partner can use the PDF viewer's own print button.
 * The app runs embedded inside monday's iframe, so a plain `window.print()` call there targets the
 * wrong frame and produces nothing usable — a real new tab is outside that sandbox and always works.
 */
export function openPdfForPrinting(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) {
    // Popup blocked — fall back to a normal download so the partner still gets the file.
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reservation.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
